import { useEffect, useState } from "react";
import api from "../../../../lib/api";
import { useDropshipPartner } from "../../../../components/DropshipWorkspaceShell";
import AgreementsAggregateSection from "./AgreementsAggregateSection";
import AgreementInlineSection from "./AgreementInlineSection";
import CatalogMirrorAggregateSection from "./CatalogMirrorAggregateSection";
import CatalogMirrorPerBuyerSection from "./CatalogMirrorPerBuyerSection";
import PricingRulesCard from "./PricingRulesCard";
import ChannelsPanel from "./ChannelsPanel";
import PendingAgreementsCallout from "../../../../components/dropship/PendingAgreementsCallout";
import EdiConnectorsSection from "./EdiConnectorsSection";

// ─────────────────────────────────────────────────────────────────────
// 1. Connect Store
// ─────────────────────────────────────────────────────────────────────
export default function ConnectStoreTab({ onChanged }) {
  const { partnerId, isAll } = useDropshipPartner();
  const [buyers, setBuyers] = useState([]);
  const [productsCount, setProductsCount] = useState(null);
  const [lastMirrorSync, setLastMirrorSync] = useState(null);

  const load = () => {
    api.get("/buyers").then(r => setBuyers(r.data || [])).catch(() => {});
    api.get("/products").then(r => setProductsCount((r.data || []).length)).catch(() => setProductsCount(0));
    // The mirror is driven by the Catalog app; we surface a rough "last activity" tag.
    setLastMirrorSync("Real-time");
  };
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line no-unused-vars
  const _onChangedRef = onChanged;

  const selectedBuyer = !isAll ? buyers.find(b => b.id === partnerId) : null;

  return (
    <div className="space-y-8" data-testid="dropship-setup-tab">
      {/* Feb 2026 · Iter 208 · Pending signature callout — surfaces
          drafts requiring the seller's attention (or blocked on the
          buyer) so agreements don't stall silently. */}
      {isAll && <PendingAgreementsCallout role="seller" />}

      {/* Row 0 · "How dropship works" — aggregate only (hidden when a buyer is picked so per-buyer view stays focused) */}
      {isAll && (
        <div>
          <div className="text-xs mono uppercase tracking-[0.18em] text-foreground/55 mb-4">How dropship works</div>
          <div className="grid lg:grid-cols-4 gap-2 items-start">
            {[
              { n: 1, l: "Mirror your catalog",   d: "Your Catalog app is the source of truth. Products auto-mirror into dropship with MSRP, MAP, wholesale, and handling fees." },
              { n: 2, l: "Sign the agreement",    d: "Bilateral contract per buyer — returns, delays, damaged-order policy, and workflow mode. Both parties sign to lock terms." },
              { n: 3, l: "Buyer connects & mirrors", d: "Once signed, your catalog auto-flows to the buyer's dropship view with the pricing you set. They pick what to sell." },
              { n: 4, l: "Daily fulfilment & auto-invoice", d: "Buyer's end-customer orders flow into your Orders tab. Confirm shipments; tracking auto-pushes to their channel. Nightly consolidated invoice." },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex flex-col gap-2.5" data-testid={`setup-step-${s.n}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-secondary text-foreground/70 grid place-items-center text-xs font-medium">{s.n}</div>
                  {i < arr.length - 1 && <div className="flex-1 h-px bg-border"/>}
                </div>
                <div className="text-sm font-medium">{s.l}</div>
                <div className="text-xs text-foreground/55">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 1 · Agreements */}
      {isAll
        ? <AgreementsAggregateSection buyers={buyers} />
        : <AgreementInlineSection buyer={selectedBuyer} />}

      {/* Section 2 · Pricing rules */}
      <PricingRulesCard buyers={buyers} scopedBuyer={selectedBuyer} isAll={isAll} />

      {/* Section 3 · Catalog mirror */}
      {isAll
        ? <CatalogMirrorAggregateSection productsCount={productsCount} lastSync={lastMirrorSync} />
        : <CatalogMirrorPerBuyerSection buyer={selectedBuyer} productsCount={productsCount} lastSync={lastMirrorSync} />}

      {/* Section 4 · Direct sales channels (Feb 2026 · Iter 205)
          Peer concept to Wholesale buyers — sellers who also run their
          own marketplace / DTC storefronts wire them here so their
          direct orders flow into the same Fulfilment queue. Only shown
          on the aggregate view since channels are seller-owned, not
          per-buyer. */}
      {isAll && (
        <div className="pt-4 border-t border-border">
          <ChannelsPanel />
        </div>
      )}

      {/* Section 5 · EDI trading partners (Iter 209 · Phase B)
          Retailer connectors (Macy's, Nordstrom, TJX, Target Plus, …)
          rendered as PEERS of the sales-channels above. Aggregate view
          only — each card opens a right-side sheet with the full
          BuyerEdiPanel (settings + 850 ingest + 855/856/810 emit +
          Phase-2 automation teaser). */}
      {isAll && (
        <div className="pt-4 border-t border-border">
          <EdiConnectorsSection buyers={buyers} />
        </div>
      )}
    </div>
  );
}
